import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Instagram, MessageCircle, Send, Loader2 } from "lucide-react";
import { CONTACT, whatsappLink } from "@/site-data";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-[#2E4390] bg-[#0D1B5E] px-4 py-3 text-sm text-[#F8FAFC] placeholder-[#8B9AC4] outline-none transition-colors duration-300 focus:border-[#1E7FFF]";

const EMPTY = { full_name: "", email: "", phone: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Mesajınız alındı! En kısa sürede size dönüş yapacağız.");
      setForm(EMPTY);
    } catch {
      toast.error("Mesaj gönderilemedi. Lütfen tekrar deneyin veya WhatsApp'tan yazın.");
    } finally {
      setLoading(false);
    }
  };

  const infoRows = [
    { icon: Phone, label: "TELEFON", value: CONTACT.phoneDisplay, href: `tel:+${CONTACT.phoneRaw}`, testid: "contact-info-phone" },
    { icon: Mail, label: "E-POSTA", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-info-email" },
    { icon: Instagram, label: "INSTAGRAM", value: CONTACT.instagram, href: CONTACT.instagramUrl, testid: "contact-info-instagram" },
  ];

  return (
    <section id="iletisim" className="relative py-24 md:py-32">
      <div className="pointer-events-none absolute -left-32 top-1/3 h-[420px] w-[420px] rounded-full bg-[#1E7FFF]/15 blur-[140px]" />
      <div className="relative mx-auto grid max-w-7xl gap-14 px-5 md:px-8 lg:grid-cols-2">
        <div>
          <Reveal>
            <Eyebrow>İletişim</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-syne text-3xl font-bold tracking-tight md:text-5xl">
              Birlikte <span className="text-[#1E7FFF]">Büyütelim.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-5 max-w-md text-base leading-relaxed text-[#B9C4E8]">
              Projenizi anlatın; 24 saat içinde size özel bir ön analiz ve yol haritası ile dönelim.
              Dilerseniz doğrudan WhatsApp üzerinden de yazabilirsiniz.
            </p>
          </Reveal>

          <div className="mt-9 space-y-4">
            {infoRows.map((r, i) => (
              <Reveal key={r.label} delay={0.1 * i}>
                <a
                  href={r.href}
                  target={r.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  data-testid={r.testid}
                  className="group flex items-center gap-4 rounded-2xl border border-[#2E4390] bg-[#122263]/60 p-4 transition-all duration-300 hover:border-[#1E7FFF]"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#1E7FFF]/15 text-[#5CA3FF] transition-transform duration-300 group-hover:scale-110">
                    <r.icon size={18} />
                  </span>
                  <div>
                    <p className="font-code text-[10px] tracking-[0.25em] text-[#8B9AC4]">{r.label}</p>
                    <p className="text-sm font-semibold text-[#F8FAFC]">{r.value}</p>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.35}>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-[#052E16] transition-transform duration-300 hover:scale-105"
            >
              <MessageCircle size={16} />
              WhatsApp'tan Hemen Yaz
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-3xl border border-[#2E4390] bg-[#1A2E78]/80 p-7 shadow-[0_0_50px_rgba(30,127,255,0.15)] backdrop-blur-md md:p-9"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="font-code mb-2 block text-[10px] tracking-[0.25em] uppercase text-[#8B9AC4]">Ad Soyad</label>
                <input
                  data-testid="contact-form-name-input"
                  required
                  value={form.full_name}
                  onChange={set("full_name")}
                  placeholder="Adınız Soyadınız"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="font-code mb-2 block text-[10px] tracking-[0.25em] uppercase text-[#8B9AC4]">E-posta</label>
                <input
                  data-testid="contact-form-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="ornek@firma.com"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="font-code mb-2 block text-[10px] tracking-[0.25em] uppercase text-[#8B9AC4]">Telefon</label>
                <input
                  data-testid="contact-form-phone-input"
                  required
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="05XX XXX XX XX"
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="font-code mb-2 block text-[10px] tracking-[0.25em] uppercase text-[#8B9AC4]">Mesajınız</label>
                <textarea
                  data-testid="contact-form-message-input"
                  required
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Projenizden ve hedeflerinizden kısaca bahsedin..."
                  className={`${inputCls} resize-none`}
                />
              </div>
            </div>
            <button
              data-testid="contact-form-submit-btn"
              type="submit"
              disabled={loading}
              className="group mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-[#1E7FFF] px-7 py-4 text-sm font-semibold text-white shadow-[0_0_30px_rgba(30,127,255,0.4)] transition-all duration-300 hover:bg-[#5CA3FF] disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
              {loading ? "Gönderiliyor..." : "Mesajı Gönder"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
